import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EMPLOYMENT_TYPES } from "@/lib/usStates";
import { useEmployeeDefaultSettings } from "@/hooks/useEmployeeDefaultSettings";
import { COMPENSATION_TYPE_OPTIONS, PAY_CYCLE_OPTIONS } from "@/lib/employeeDefaults";
import { Users, Mail, FileUp, FileText, X, Download, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Link, useLocation } from "wouter";
import { Controller, useForm } from "react-hook-form";
import { tabWithErrors, validatePersonalStep } from "@/lib/addEmployeeSteps";
import { zodResolver } from "@hookform/resolvers/zod";
import { formatDateSafe, formatPhone, formatSSNInput } from "@/lib/utils";
import { formatPhoneInputValue } from "@/lib/phone";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { AddressFields } from "@/components/forms/AddressFields";
import { EmployeeStatusSelect } from "@/components/forms/EmployeeStatusSelect";
import { composeAddressLine, defaultCountry, emptyAddressFields } from "@/lib/address";
import { downloadEmployeeImportTemplate } from "@/lib/exportUtils";
import { EmployeeExportDialog } from "@/components/employees/EmployeeExportDialog";
import { formatStatus } from "@/lib/employeeStatus";
import { sortEmployeesByFullName } from "@/lib/employeeSort";
import { insertEmployeeSchema } from "@shared/schema";
import { OrganizationSelect } from "@/components/forms/OrganizationSelect";

export interface Employee {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  department: string;
  position: string;
  status: string;
  hireDate?: string | Date | null;
  salary?: string;
  address?: string;
  address1?: string;
  city?: string | null;
  emergencyContact?: string;
  emergencyPhone?: string;
  profilePicture?: string | null;
  locationId?: number | null;
}

export function EmployeesTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: employees = [], isLoading: employeesLoading } = useQuery({
    queryKey: ["/api/agency/employees"],
  });

  const { data: profile } = useQuery({
    queryKey: ["/api/user/profile"],
    queryFn: async () => {
      const r = await fetch("/api/user/profile");
      if (!r.ok) throw new Error("Failed to load profile");
      return r.json();
    },
  });

  // Use agencyId from profile (works for both brokers and agency members)
  const agencyId = (profile?.agencyId || "").toString();

  // Load organization reference lists for the agency
  const { data: orgDepartments = [] } = useQuery({
    queryKey: ["/api/org/departments", "agency", agencyId],
    enabled: !!agencyId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/departments?ownerType=agency&ownerId=${agencyId}`
      );
      if (!res.ok) throw new Error("Failed to load departments");
      return res.json();
    },
  });

  const { data: orgJobTitles = [] } = useQuery({
    queryKey: ["/api/org/job-titles", "agency", agencyId],
    enabled: !!agencyId,
    queryFn: async () => {
      const res = await fetch(
        `/api/org/job-titles?ownerType=agency&ownerId=${agencyId}`
      );
      if (!res.ok) throw new Error("Failed to load job titles");
      return res.json();
    },
  });

  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [addEmployeeOpen, setAddEmployeeOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);
  // Editing occurs in the detail view; no local edit dialog state

  const [addEmployeeTab, setAddEmployeeTab] = useState("personal");
  
  // Sorting state
  type SortField = 'name' | 'status' | 'hireDate' | 'department' | 'position' | 'city' | 'email' | 'phone';
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-4 w-4 ml-1" /> 
      : <ChevronDown className="h-4 w-4 ml-1" />;
  };

  
  const addEmployeeForm = useForm({
    resolver: zodResolver(insertEmployeeSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      workPhone: "",
      personalEmail: "",
      phone: "",
      dateOfBirth: "",
      sex: "",
      ssn: "",
      hireDate: "",
      department: "",
      position: "",
      employmentType: "",
      salary: "",
      status: "",
      payCycle: "",
      compensationType: "",
      departmentId: null,
      jobTitleId: null,
      locationId: null,
      employeeClassId: null,
      scheduledHours: "",
      ...emptyAddressFields(),
    },
  });

  const { defaults: employeeDefaults } = useEmployeeDefaultSettings("agency");

  const preloadEmployeeDefaults = useCallback(() => {
    const currentValues = addEmployeeForm.getValues();
    if (addEmployeeForm.formState.isDirty) {
      return;
    }

    const nextValues = { ...currentValues };
    let valuesChanged = false;

    const applyIfEmpty = (
      fieldName: keyof typeof nextValues,
      candidate?: string
    ) => {
      if (!candidate || candidate.trim().length === 0) {
        return;
      }
      const current = nextValues[fieldName];
      if (typeof current === "string" && current.trim().length > 0) {
        return;
      }
      (nextValues as any)[fieldName] = candidate;
      valuesChanged = true;
    };

    applyIfEmpty("employmentType", employeeDefaults.employmentType);
    applyIfEmpty("status", employeeDefaults.status || "active");
    applyIfEmpty("payCycle", employeeDefaults.payCycle);
    applyIfEmpty("compensationType", employeeDefaults.compensationType);
    applyIfEmpty("city", employeeDefaults.city);
    applyIfEmpty("state", employeeDefaults.state);
    applyIfEmpty("county", employeeDefaults.county);

    const desiredCountry = employeeDefaults.country?.trim();
    if (desiredCountry && desiredCountry.length > 0) {
      const currentCountry = (nextValues.country || "").trim();
      if (
        currentCountry.length === 0 ||
        currentCountry.toLowerCase() === defaultCountry.toLowerCase()
      ) {
        nextValues.country = desiredCountry;
        valuesChanged = true;
      }
    }

    if (!valuesChanged) {
      return;
    }

    addEmployeeForm.reset(nextValues);
  }, [addEmployeeForm, employeeDefaults]);

  useEffect(() => {
    if (!addEmployeeOpen) {
      return;
    }
    preloadEmployeeDefaults();
  }, [addEmployeeOpen, preloadEmployeeDefaults]);

  // No edit form in this view

  // No edit form effect

  const handleAddEmployeePhoneChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const formattedPhone = formatPhoneInputValue(event.target.value);

    addEmployeeForm.setValue("phone", formattedPhone, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  // No edit phone handlers; edits happen in detail page

  // Emergency phone fields are formatted inline in form renderers

  const createEmployeeMutation = useMutation({
    mutationFn: async (employee: any) => {
      const employeeResponse = await apiRequest(
        "POST",
        "/api/employees",
        employee
      );
      const result = await employeeResponse.json();
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency/employees"] });
      toast({
        title: "Employee added",
        description: "New employee has been successfully added.",
      });
      setAddEmployeeOpen(false);
      setAddEmployeeTab("personal");
      addEmployeeForm.reset();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add employee. Please try again.",
        variant: "destructive",
      });
    },
  });

  // No update mutation in this view; editing is done in the detail page

  // No delete action in this view to match ClientEmployees UX

  const bulkImportEmployeesMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("csvFile", file);
      formData.append("agencyId", agencyId);

      const response = await fetch("/api/employees/bulk-import", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to import employees");
      }

      return response.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency/employees"] });
      const parts = [];
      if (data.created > 0) parts.push(`${data.created} created`);
      if (data.updated > 0) parts.push(`${data.updated} updated`);
      if (data.skipped > 0) parts.push(`${data.skipped} skipped`);
      
      // Show success message
      if (data.imported > 0) {
        toast({
          title: "Import successful",
          description: `Successfully processed ${data.imported} employees${parts.length > 0 ? ` (${parts.join(', ')})` : ''}.`,
          variant: "success",
          duration: 5000,
        });
      }

      // Show validation errors if any
      if (data.errors && data.errors.length > 0) {
        // Show first few errors
        const errorMessages = data.errors.slice(0, 3).join('\n');
        const moreErrors = data.errors.length > 3 ? `\n...and ${data.errors.length - 3} more errors` : '';
        
        toast({
          title: `Import validation issues (${data.errors.length})`,
          description: errorMessages + moreErrors,
          variant: "destructive",
          duration: 30000, // 30 seconds to read errors
        });
      }

      setIsImportDialogOpen(false);
      setCsvFile(null);
      setCsvData([]);
      setImportPreview([]);
    },
    onError: (error: any) => {
      console.error("Bulk import error:", error);
      toast({ 
        title: "Failed to import employees", 
        description: error.message || "An error occurred during import",
        variant: "destructive" 
      });
    },
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "text/csv") {
      setCsvFile(file);
      parseCsvFile(file);
    } else {
      toast({
        title: "Please select a valid CSV file",
        variant: "destructive",
      });
    }
  };

  // CSV processing functions for preview
  const parseCsvFile = (file: File) => {
    setIsProcessingCsv(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split("\n");
      const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));

      const data = lines
        .slice(1)
        .filter((line) => line.trim())
        .map((line) => {
          const values = line.split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });
          return row;
        });

      setCsvData(data);
      setImportPreview(data.slice(0, 5)); // Show first 5 rows for preview
      setIsProcessingCsv(false);
    };
    reader.readAsText(file);
  };

  const handleImportEmployees = () => {
    if (!csvFile) {
      toast({ title: "No file selected", variant: "destructive" });
      return;
    }

    bulkImportEmployeesMutation.mutate(csvFile);
  };

  const handleAddEmployee = (values: any) => {
    // Look up the names from the selected IDs to populate legacy text fields
    const selectedJobTitle = values.jobTitleId
      ? orgJobTitles.find((jt: any) => jt.id === values.jobTitleId)
      : null;
    const selectedDepartment = values.departmentId
      ? orgDepartments.find((d: any) => d.id === values.departmentId)
      : null;

    const employeePayload = {
      ...values,
      agencyId: agencyId, // Link employee to the agency
      address: composeAddressLine(values),
      // Populate both ID fields and legacy text fields
      departmentId: values.departmentId ?? null,
      jobTitleId: values.jobTitleId ?? null,
      locationId: values.locationId ?? null,
      employeeClassId: values.employeeClassId ?? null,
      // Update legacy text fields with the selected names
      position: selectedJobTitle?.name || values.position || null,
      department: selectedDepartment?.name || values.department || null,
    };

    createEmployeeMutation.mutate(employeePayload);
  };
  // No local edit submit handler
  // Profile picture handling not included to keep parity with ClientEmployees table
  // Navigate to detail page for editing
  const [, navigate] = useLocation();
  const goToDetail = (id: number) => navigate(`/agency/employees/${id}`);

  const baseSortedEmployees = useMemo(() => {
    return sortEmployeesByFullName((employees as any[]) || []);
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    const trimmedSearchTerm = employeeSearchTerm.trim();
    if (trimmedSearchTerm.length === 0) {
      return baseSortedEmployees;
    }

    const normalizedSearch = trimmedSearchTerm.toLowerCase();
    const employeeMatchesSearch = (employee: any) => {
      return (
        employee.firstName?.toLowerCase().includes(normalizedSearch) ||
        employee.lastName?.toLowerCase().includes(normalizedSearch) ||
        employee.email?.toLowerCase().includes(normalizedSearch) ||
        employee.department?.toLowerCase().includes(normalizedSearch) ||
        employee.position?.toLowerCase().includes(normalizedSearch)
      );
    };

    return baseSortedEmployees.filter(employeeMatchesSearch);
  }, [baseSortedEmployees, employeeSearchTerm]);

  const sortedEmployees = useMemo(() => {
    if (!sortField) return filteredEmployees;
    
    return [...filteredEmployees].sort((a: any, b: any) => {
      let aValue: string | number | Date | null;
      let bValue: string | number | Date | null;
      
      switch (sortField) {
        case 'name':
          aValue = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
          bValue = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'hireDate':
          aValue = a.hireDate ? new Date(a.hireDate).getTime() : 0;
          bValue = b.hireDate ? new Date(b.hireDate).getTime() : 0;
          break;
        case 'department':
          aValue = (a.department || '').toLowerCase();
          bValue = (b.department || '').toLowerCase();
          break;
        case 'position':
          aValue = (a.position || '').toLowerCase();
          bValue = (b.position || '').toLowerCase();
          break;
        case 'city':
          aValue = (a.city || '').toLowerCase();
          bValue = (b.city || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'phone':
          aValue = (a.phone || '').toLowerCase();
          bValue = (b.phone || '').toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredEmployees, sortField, sortDirection]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Agency Employees</h2>
        <div className="flex items-center space-x-2">
          <Input
            placeholder="Search employees..."
            value={employeeSearchTerm}
            onChange={(e) => setEmployeeSearchTerm(e.target.value)}
            className="w-64"
          />
          <EmployeeExportDialog
            employees={sortedEmployees}
            filenameBase="agency_employees"
          />
          <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
            <FileUp className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Dialog open={addEmployeeOpen} onOpenChange={setAddEmployeeOpen}>
            <DialogTrigger asChild>
              <Button>
                <Users className="h-4 w-4 mr-2" />
                Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Employee</DialogTitle>
              </DialogHeader>
              <Form {...addEmployeeForm}>
                <form
                  onSubmit={addEmployeeForm.handleSubmit(handleAddEmployee, (errors) =>
                    setAddEmployeeTab(tabWithErrors(errors))
                  )}
                  className="space-y-4"
                >
                  <Tabs value={addEmployeeTab} onValueChange={setAddEmployeeTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-4">
                      <TabsTrigger value="personal">Personal</TabsTrigger>
                      <TabsTrigger value="employment">Employment</TabsTrigger>
                    </TabsList>

                    {/* Personal Tab */}
                    <TabsContent value="personal" className="space-y-4 mt-0">
                      {/* Row 1: Name */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={addEmployeeForm.control}
                          name="firstName"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>First Name <span className="text-red-500">*</span></FormLabel>
                              <FormControl>
                                <Input placeholder="John" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={addEmployeeForm.control}
                          name="lastName"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Last Name <span className="text-red-500">*</span></FormLabel>
                              <FormControl>
                                <Input placeholder="Doe" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 2: Personal Email + Phone */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={addEmployeeForm.control}
                          name="personalEmail"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-8">
                              <FormLabel>Personal Email</FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="john.doe@gmail.com"
                                  {...field}
                                  value={field.value || ""}
                                  onBlur={(e) =>
                                    field.onChange(
                                      e.target.value.trim().toLowerCase()
                                    )
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={addEmployeeForm.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-4">
                              <FormLabel>Phone</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value || ""}
                                  placeholder="(555) 123-4567"
                                  onChange={(e) =>
                                    handleAddEmployeePhoneChange(e as any)
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 3: Date of Birth + Sex + SSN */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={addEmployeeForm.control}
                          name="dateOfBirth"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-4">
                              <FormLabel>Date of Birth</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} value={field.value || ""} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={addEmployeeForm.control}
                          name="sex"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-4">
                              <FormLabel>Sex</FormLabel>
                              <Select
                                value={field.value || ""}
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select sex" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="male">Male</SelectItem>
                                  <SelectItem value="female">Female</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={addEmployeeForm.control}
                          name="ssn"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-4">
                              <FormLabel>SSN</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value || ""}
                                  onChange={(e) => field.onChange(formatSSNInput(e.target.value))}
                                  inputMode="numeric"
                                  maxLength={11}
                                  placeholder="123-45-6789"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Address */}
                      <AddressFields control={addEmployeeForm.control as any} />

                      {/* Cancel + Next Buttons */}
                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setAddEmployeeOpen(false);
                            setAddEmployeeTab("personal");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={async () => {
                            if (await validatePersonalStep(addEmployeeForm)) setAddEmployeeTab("employment");
                          }}
                        >
                          Next
                        </Button>
                      </div>
                    </TabsContent>

                    {/* Employment Tab */}
                    <TabsContent value="employment" className="space-y-4 mt-0">
                      {/* Row 1: Work Email + Work Phone */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={addEmployeeForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-8">
                              <FormLabel>Work Email <span className="text-red-500">*</span></FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="john.doe@company.com"
                                  {...field}
                                  onBlur={(e) =>
                                    field.onChange(
                                      e.target.value.trim().toLowerCase()
                                    )
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={addEmployeeForm.control}
                          name="workPhone"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-4">
                              <FormLabel>Work Phone</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value || ""}
                                  placeholder="(555) 123-4567"
                                  onChange={(e) => {
                                    const formatted = formatPhoneInputValue(e.target.value);
                                    field.onChange(formatted);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 2: Department + Job Title */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={addEmployeeForm.control}
                          name="departmentId"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Department</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="department"
                                  ownerType="agency"
                                  ownerId={agencyId}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Department"
                                  disabled={!agencyId}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={addEmployeeForm.control}
                          name="jobTitleId"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Job Title</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="job-title"
                                  ownerType="agency"
                                  ownerId={agencyId}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Job Title"
                                  disabled={!agencyId}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 3: Location + Employee Class */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={addEmployeeForm.control}
                          name="locationId"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Location</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="location"
                                  ownerType="agency"
                                  ownerId={agencyId}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Location"
                                  disabled={!agencyId}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={addEmployeeForm.control}
                          name="employeeClassId"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Employee Class</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="employee-class"
                                  ownerType="agency"
                                  ownerId={agencyId}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Employee Class"
                                  disabled={!agencyId}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 4: Hire Date + Salary */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={addEmployeeForm.control}
                          name="hireDate"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Hire Date</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={addEmployeeForm.control}
                          name="salary"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Salary</FormLabel>
                              <FormControl>
                                <Input placeholder="$65,000" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 5: Scheduled Hours */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={addEmployeeForm.control}
                          name="scheduledHours"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Hours Worked (Weekly)</FormLabel>
                              <FormControl>
                                <Input placeholder="40" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 6: Compensation Type + Pay Cycle */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={addEmployeeForm.control}
                          name="compensationType"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Compensation Type</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || ""}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select compensation type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {COMPENSATION_TYPE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={addEmployeeForm.control}
                          name="payCycle"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Pay Cycle</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || ""}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select pay cycle" />
                                </SelectTrigger>
                                <SelectContent>
                                  {PAY_CYCLE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 7: Employment Type + Status */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={addEmployeeForm.control}
                          name="employmentType"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Employment Type</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value || ""}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select a type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {EMPLOYMENT_TYPES.map((t) => (
                                    <SelectItem key={t} value={t}>
                                      {t}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={addEmployeeForm.control}
                          name="status"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Status <span className="text-red-500">*</span></FormLabel>
                              <EmployeeStatusSelect
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Select status"
                              />
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Cancel + Add Employee Buttons */}
                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setAddEmployeeOpen(false);
                            setAddEmployeeTab("personal");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button type="submit">Add Employee</Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {employeesLoading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredEmployees.length > 0 ? (
        <div className="bg-white dark:bg-gray-900 shadow overflow-hidden sm:rounded-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Employee Name
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      {getSortIcon('status')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('hireDate')}
                  >
                    <div className="flex items-center">
                      Hire Date
                      {getSortIcon('hireDate')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('department')}
                  >
                    <div className="flex items-center">
                      Department
                      {getSortIcon('department')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('position')}
                  >
                    <div className="flex items-center">
                      Title
                      {getSortIcon('position')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('city')}
                  >
                    <div className="flex items-center">
                      Location
                      {getSortIcon('city')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center">
                      Email
                      {getSortIcon('email')}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('phone')}
                  >
                    <div className="flex items-center">
                      Phone
                      {getSortIcon('phone')}
                    </div>
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedEmployees.map((employee: any) => (
                  <tr
                    key={employee.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                    onClick={() => goToDetail(employee.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                              {employee.firstName?.[0] || ""}
                              {employee.lastName?.[0] || ""}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <Link href={`/agency/employees/${employee.id}`}>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer">
                              {employee.firstName} {employee.lastName}
                            </div>
                          </Link>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          (employee.status || "").toString().toLowerCase() ===
                          "active"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                            : (employee.status || "").toString().toLowerCase() ===
                              "inactive"
                            ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {formatStatus(employee.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {employee.hireDate
                        ? formatDateSafe(employee.hireDate as any)
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {(employee as any).employmentType || (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                          Missing
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {employee.department || "Not specified"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {employee.position || "Not specified"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {employee.city || ""}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {employee.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {employee.phone ? formatPhone(employee.phone) : "N/A"}
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 shadow sm:rounded-md p-6 text-center">
          <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No employees</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Get started by adding your first employee.
          </p>
          <div className="mt-6">
            <Button onClick={() => setAddEmployeeOpen(true)}>
              <Users className="h-4 w-4 mr-2" />
              Add Employee
            </Button>
          </div>
        </div>
      )}

      {/* Import Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Import Employees from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with employee data. Download the template to see the required format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Template Download Section */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Download the template to see the required format for importing employees.
              </p>
              <Button
                variant="outline"
                onClick={() => downloadEmployeeImportTemplate()}
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Import Template
              </Button>
            </div>

            {/* File Upload Section */}
            <div className="space-y-4 border-t pt-4">
              {!csvFile && (
                <div className="flex items-center justify-center w-full">
                  <label
                    htmlFor="csv-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-5">
                      <FileUp className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        <span className="font-semibold">Click to upload</span>{" "}
                        your CSV file
                      </p>
                    </div>
                    <input
                      id="csv-upload"
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                  </label>
                </div>
              )}

              {csvFile && (
                <div className="flex items-center space-x-2 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg relative">
                  <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium">{csvFile.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({csvData.length} rows)
                  </span>
                  <button
                    className="absolute top-1/2 right-2 transform -translate-y-1/2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
                    onClick={() => {
                      const fileInput = document.getElementById(
                        "csv-upload"
                      ) as HTMLInputElement;
                      if (fileInput) fileInput.value = ""; // Reset file input
                      setCsvFile(null);
                      setCsvData([]);
                      setImportPreview([]);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Preview Section */}
            {isProcessingCsv && (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2">Processing CSV...</span>
              </div>
            )}

            {importPreview.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium">Preview (First 5 rows)</h4>
                <div className="overflow-x-auto border rounded-lg dark:border-gray-700">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          First Name
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Last Name
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Email
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Department
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Position
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Hire Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {importPreview.map((row, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm">
                            {row["First Name"] || row.firstName || "-"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {row["Last Name"] || row.lastName || "-"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {row["Email"] || row.email || "-"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {row["Department"] || row.department || "-"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {row["Position"] || row.position || "-"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {row["Hire Date"] || row.hireDate || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="text-sm text-muted-foreground">
                  <p>
                    <strong>Total rows to import:</strong> {csvData.length}{" "}
                    <strong className="ml-2">Valid employees:</strong>{" "}
                    {
                      csvData.filter(
                        (row) => (row["First Name"] || row.firstName) && (row["Last Name"] || row.lastName)
                      ).length
                    }
                  </p>
                  {csvData.length >
                    csvData.filter(
                      (row) => (row["First Name"] || row.firstName) && (row["Last Name"] || row.lastName)
                    ).length && (
                    <p className="text-yellow-600">
                      <strong>Note:</strong>{" "}
                      {csvData.length -
                        csvData.filter(
                          (row) => (row["First Name"] || row.firstName) && (row["Last Name"] || row.lastName)
                        ).length}{" "}
                      rows will be skipped (missing required fields: First Name, Last Name)
                    </p>
                  )}
                  {csvData.some((row) => (row["First Name"] || row.firstName) && (row["Last Name"] || row.lastName) && !(row["Email"] || row.email)) && (
                    <p className="text-blue-600 mt-1">
                      <strong>Info:</strong> Some employees are missing email addresses. They will be imported without login credentials.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsImportDialogOpen(false);
                setCsvFile(null);
                setCsvData([]);
                setImportPreview([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImportEmployees}
              disabled={!csvFile || bulkImportEmployeesMutation.isPending}
            >
              {bulkImportEmployeesMutation.isPending
                ? "Importing..."
                : "Import Employees"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog removed; navigate to detail page instead */}
    </div>
  );
}


